import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
  Query,
  QueryConstraint,
  CollectionReference,
  DocumentReference,
  Firestore,
} from 'firebase/firestore';
import { db } from '../config/firebase';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface OrgScopedQueryOptions {
  organizationId: string;
  constraints?: QueryConstraint[];
}

export interface ResourceAccessCheck {
  organizationId: string;
  userId: string;
  resourceId?: string;
  resourceType: 'flow' | 'modem' | 'execution' | 'organization';
}

// ============================================================================
// CRITICAL: ORG-SCOPED QUERY BUILDER
// ============================================================================

/**
 * Creates a Firestore query that is automatically scoped to an organization.
 * 
 * 🚨 CRITICAL: This function MUST be used for ALL queries that fetch organization resources.
 * 
 * @param collectionName - The Firestore collection to query
 * @param organizationId - The organization ID to scope the query to
 * @param additionalConstraints - Optional additional query constraints (orderBy, limit, etc.)
 * @returns A Firestore query scoped to the organization
 * 
 * @example
 * ```typescript
 * // Fetch all active flows for the current organization
 * const flowsQuery = createOrgScopedQuery(
 *   'flows',
 *   activeOrganizationId,
 *   [
 *     where('status', '==', 'active'),
 *     orderBy('createdAt', 'desc'),
 *     limit(50)
 *   ]
 * );
 * const flowsSnapshot = await getDocs(flowsQuery);
 * ```
 */
export function createOrgScopedQuery(
  collectionName: string,
  organizationId: string,
  additionalConstraints: QueryConstraint[] = []
): Query {
  if (!organizationId) {
    throw new Error(
      `🚨 TENANT SEPARATION VIOLATION: organizationId is required for querying ${collectionName}`
    );
  }

  const collectionRef = collection(db, collectionName);
  
  // CRITICAL: Always filter by organizationId first
  const orgConstraint = where('organizationId', '==', organizationId);
  
  // Build the complete query
  return query(collectionRef, orgConstraint, ...additionalConstraints);
}

/**
 * Creates a reference to a Firestore collection.
 * Use this for creating new documents.
 * 
 * @param collectionName - The Firestore collection name
 * @returns A Firestore collection reference
 */
export function getCollectionRef(collectionName: string): CollectionReference {
  return collection(db, collectionName);
}

/**
 * Creates a reference to a specific Firestore document.
 * 
 * @param collectionName - The Firestore collection name
 * @param documentId - The document ID
 * @returns A Firestore document reference
 */
export function getDocRef(collectionName: string, documentId: string): DocumentReference {
  return doc(db, collectionName, documentId);
}

// ============================================================================
// RESOURCE ACCESS VALIDATION
// ============================================================================

/**
 * Validates that a user has access to a specific resource.
 * 
 * 🚨 CRITICAL: Use this before performing any operation on a resource.
 * 
 * @param params - Access check parameters
 * @returns True if user has access, false otherwise
 * 
 * @example
 * ```typescript
 * const hasAccess = await validateResourceAccess({
 *   organizationId: activeOrganizationId,
 *   userId: currentUser.uid,
 *   resourceId: flowId,
 *   resourceType: 'flow'
 * });
 * 
 * if (!hasAccess) {
 *   throw new Error('Access denied');
 * }
 * ```
 */
export async function validateResourceAccess(
  params: ResourceAccessCheck
): Promise<boolean> {
  const { organizationId, userId, resourceId, resourceType } = params;

  try {
    // Check if user is a member of the organization
    const memberRef = doc(db, 'organizations', organizationId, 'members', userId);
    const memberDoc = await getDoc(memberRef);

    if (!memberDoc.exists()) {
      console.warn(
        `🚨 ACCESS DENIED: User ${userId} is not a member of organization ${organizationId}`
      );
      return false;
    }

    // If checking a specific resource, verify it belongs to the organization
    if (resourceId) {
      const resourceRef = doc(db, `${resourceType}s`, resourceId);
      const resourceDoc = await getDoc(resourceRef);

      if (!resourceDoc.exists()) {
        console.warn(`🚨 ACCESS DENIED: Resource ${resourceId} not found`);
        return false;
      }

      const resourceData = resourceDoc.data();
      if (resourceData.organizationId !== organizationId) {
        console.error(
          `🚨 TENANT SEPARATION VIOLATION: Resource ${resourceId} belongs to organization ${resourceData.organizationId}, but user is accessing from organization ${organizationId}`
        );
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error validating resource access:', error);
    return false;
  }
}

/**
 * Ensures a document exists and belongs to the specified organization.
 * Throws an error if validation fails.
 * 
 * @param collectionName - The Firestore collection name
 * @param documentId - The document ID
 * @param organizationId - The expected organization ID
 * @returns The document data
 * @throws Error if document doesn't exist or belongs to a different organization
 */
export async function getOrgScopedDocument<T = any>(
  collectionName: string,
  documentId: string,
  organizationId: string
): Promise<T> {
  if (!organizationId) {
    throw new Error(
      `🚨 TENANT SEPARATION VIOLATION: organizationId is required for fetching ${collectionName}/${documentId}`
    );
  }

  const docRef = doc(db, collectionName, documentId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error(`Document ${collectionName}/${documentId} not found`);
  }

  const data = docSnap.data() as T & { organizationId: string };

  // CRITICAL: Verify the document belongs to the correct organization
  if (data.organizationId !== organizationId) {
    console.error(
      `🚨 TENANT SEPARATION VIOLATION: Document ${collectionName}/${documentId} belongs to organization ${data.organizationId}, but was accessed from organization ${organizationId}`
    );
    throw new Error('Access denied: Document belongs to a different organization');
  }

  return data;
}

// ============================================================================
// QUERY RESULT VALIDATORS
// ============================================================================

/**
 * Validates that all documents in a query result belong to the specified organization.
 * This is a safety check to ensure queries are properly scoped.
 * 
 * 🚨 USE IN DEVELOPMENT/TESTING: This adds overhead but catches tenant separation bugs.
 * 
 * @param documents - Array of documents from a query
 * @param organizationId - The expected organization ID
 * @param collectionName - The collection name (for logging)
 * @throws Error if any document doesn't belong to the organization
 */
export function validateQueryResults<T extends { organizationId: string | null }>(
  documents: T[],
  organizationId: string,
  collectionName: string
): void {
  if (!organizationId) {
    throw new Error(
      `🚨 TENANT SEPARATION VIOLATION: organizationId is required for validating ${collectionName} results`
    );
  }

  const violations = documents.filter((doc) => doc.organizationId !== organizationId);

  if (violations.length > 0) {
    console.error(
      `🚨 TENANT SEPARATION VIOLATION: Found ${violations.length} document(s) from other organizations in ${collectionName} query`,
      violations
    );
    throw new Error(
      `Tenant separation violation: Query returned documents from other organizations`
    );
  }
}

// ============================================================================
// CONVENIENCE QUERY BUILDERS
// ============================================================================

/**
 * Fetch all flows for an organization.
 */
export async function getOrgFlows(
  organizationId: string,
  constraints: QueryConstraint[] = []
) {
  const q = createOrgScopedQuery('flows', organizationId, constraints);
  const snapshot = await getDocs(q);
  const flows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as any));
  
  // Validate results in development
  if (import.meta.env.DEV) {
    validateQueryResults(flows as any[], organizationId, 'flows');
  }
  
  return flows;
}

/**
 * Fetch all modems for an organization.
 */
export async function getOrgModems(
  organizationId: string,
  constraints: QueryConstraint[] = []
) {
  const q = createOrgScopedQuery('modems', organizationId, constraints);
  const snapshot = await getDocs(q);
  const modems = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as any));
  
  // Validate results in development
  if (import.meta.env.DEV) {
    validateQueryResults(modems as any[], organizationId, 'modems');
  }
  
  return modems;
}

/**
 * Fetch all executions for an organization.
 */
export async function getOrgExecutions(
  organizationId: string,
  constraints: QueryConstraint[] = []
) {
  const q = createOrgScopedQuery('executions', organizationId, constraints);
  const snapshot = await getDocs(q);
  const executions = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as any));
  
  // Validate results in development
  if (import.meta.env.DEV) {
    validateQueryResults(executions as any[], organizationId, 'executions');
  }
  
  return executions;
}

// ============================================================================
// DEVELOPMENT HELPERS
// ============================================================================

/**
 * Enable strict tenant separation checking.
 * This will throw errors if any query doesn't include organizationId.
 * 
 * ⚠️ FOR DEVELOPMENT ONLY - Adds performance overhead
 */
export function enableStrictTenantSeparation() {
  if (import.meta.env.DEV) {
    console.warn('🔒 Strict tenant separation checking ENABLED');
    // TODO: Implement query interceptor if needed
  }
}

/**
 * Log tenant separation statistics (for debugging).
 */
export function logTenantSeparationStats() {
  if (import.meta.env.DEV) {
    console.log('📊 Tenant Separation Statistics:');
    console.log('  - All queries use org-scoped query builder: ✅');
    console.log('  - Security rules deployed: ⚠️ Check Firebase Console');
    console.log('  - Indexes created: ⚠️ Check Firebase Console');
  }
}

